/**
 * /es/product-discovery — product discovery concept page (Spanish).
 * Links /es/transcribir-entrevista + /es/registro-de-decisiones.
 */
import { MarketingShell } from './MarketingShell';

export function EsProductDiscovery() {
  return (
    <MarketingShell
      currentLocale="es"
      hreflangPath="/product-discovery"
      hreflangLocales={['es']}
      documentTitle="Product discovery: qué es, métodos y cómo aplicarlo · Draftlize"
      documentDescription="El product discovery (descubrimiento de producto) en la práctica: métodos de entrevista, síntesis de insights y cómo convertirlos en decisiones trazables."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guía · Discovery</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 56px)', lineHeight: 1.05, margin: '12px 0 24px' }}>
            Product discovery<br />
            <em>— del insight a la decisión.</em>
          </h1>
          <p className="lede">
            El product discovery (descubrimiento de producto) es la actividad de confirmar <em>qué</em> construir antes de construirlo: entrevistas, observación y validación para convertir supuestos en hechos. Incluso cuando se hace bien, el punto débil está en el mismo sitio: los <em>insights se pierden antes de convertirse en decisiones</em>. Aquí, los métodos y cómo transformar los hallazgos en decisiones trazables.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Métodos de discovery.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>Entrevistas de usuario</strong> — escucha el problema y el contexto, no respuestas.</li>
            <li><strong>Observación y datos de conducta</strong> — mira lo que hacen, no solo lo que dicen.</li>
            <li><strong>Validación (prototipos, experimentos)</strong> — prueba hipótesis a pequeña escala.</li>
            <li><strong>Síntesis</strong> — condensa hallazgos sueltos en patrones suficientes para decidir.</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            Empieza por <a href="/es/transcribir-entrevista" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>transcribir la entrevista</a> para tener materia prima. Pero el verbatim no es la meta: es el punto de partida hacia la síntesis y la decisión.
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">El eslabón débil: entre hallazgo y decisión. <em>Conéctalo.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            El resultado del discovery no son las frases de las entrevistas, sino las <em>decisiones</em> que tomas a partir de ellas. Muchos equipos resumen los hallazgos en una presentación y ahí queda: la decisión y su porqué no viven en ningún sitio. Draftlize enlaza «verbatim → decisión → requisito».
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Por qué se pierden los insights</h3>
            <p>Los momentos clave de la entrevista se dispersan en páginas de Notion o actas, y al siguiente sprint nadie los relee, porque el «porqué» de la decisión queda desconectado del hallazgo.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Conecta «verbatim → decisión → requisito»</h3>
            <p>En Draftlize enlazas la frase de un usuario con la decisión que provocó y el requisito que de ella deriva. Si una validación posterior revierte el supuesto, lo dependiente se marca «stale».</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Tu agente de IA parte de los hallazgos</h3>
            <p>Las decisiones del discovery se vuelven contexto que Claude Code o Cursor leen vía MCP. El siguiente spec se escribe sabiendo «el usuario dijo esto, por eso decidimos aquello». Sigue con <a href="/es/registro-de-decisiones" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>registro de decisiones</a>.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          El valor del discovery no está en cuántos insights reúnes, sino en si seis meses después aún puedes rastrear las decisiones que tomaste con ellos.
        </blockquote>
        <cite>Conecta el hallazgo con la decisión y avisa cuando cambia el supuesto. Eso es Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Preguntas frecuentes.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: '¿Discovery o delivery?', a: 'El discovery confirma «si construir lo correcto»; el delivery es «construirlo bien». Van juntos, pero si las decisiones del discovery no quedan, el delivery apunta mal.' },
            { q: '¿Cuántas entrevistas?', a: 'Más que un número exacto, hasta que dejen de aparecer hallazgos nuevos (saturación). Lo importante no es la cantidad, sino con qué decisión enlazaste cada frase.' },
            { q: '¿Cómo sintetizo los hallazgos?', a: 'Agrupa las frases por tema y busca patrones suficientes para decidir. En Draftlize cada patrón se vuelve una tarjeta de decisión enlazada a las frases originales.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Conecta la frase de una entrevista con la decisión <em>— gratis con $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>Regístrate y recibe $5</span>
          <span>Paga solo por lo que usas</span>
          <span>El saldo nunca caduca</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Convierte los hallazgos del discovery en tarjetas «verbatim → decisión → requisito» en Draftlize. Si cambia el supuesto, se marca «stale» solo, y tu agente de IA lo lee.
          </p>
          <a className="btn-primary" href="/">Empieza gratis con $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
